import { WorkflowSummary } from '@opallabs/concepts'
import {
  type CalendarItem,
  type CalendarItemKind,
  type CalendarFilterCategory,
  type CalendarFilterOption,
  type CalendarFilterOptionGroup,
  type CalendarFilterState,
  type CalendarWorkflowSummaries,
} from './types'

/**
 * The categories every calendar surface can offer. An adapter injects the
 * option data and may append categories of its own, so this list is a starting
 * point rather than the whole world.
 */
export const CalendarFilterCategoryIds = {
  board: 'board',
  /** Channels and the accounts under them, in one category. */
  channels: 'channels',
  label: 'label',
  /** The days an item is live on its channel. */
  live: 'live',
  privacy: 'privacy',
  status: 'status',
  story: 'story',
} as const

export type CalendarFilterCategoryId =
  (typeof CalendarFilterCategoryIds)[keyof typeof CalendarFilterCategoryIds]

/**
 * The two privacy states, named the way the moment modal names them. A person
 * sets the state there and filters by it here, so the two surfaces have to use
 * the one pair of words.
 */
export const CALENDAR_PRIVACY_OPTIONS: readonly CalendarFilterOption[] = [
  { id: 'private', label: 'Specific people & spaces' },
  { id: 'public', label: 'Entire Workspace' },
]

/** Where a moment's workflow summary is filed, for `CalendarWorkflowSummaries`. */
export const workflowSummaryKey = (
  kind: CalendarItemKind,
  id: string
): string => `${kind}:${id}`

/**
 * A moment holding no content at all. It is an idea rather than a state of
 * work, and the payload says so without anyone having to read a workflow.
 */
export const CALENDAR_IDEA_VALUE = 'idea'

/** Kept apart from the content vocabulary, which has words of its own. */
const workflowValue = (summary: WorkflowSummary) => `workflow:${summary}`

/**
 * The two halves of the status vocabulary.
 *
 * Each is a row a person can choose as well as the heading its words sit
 * under, exactly as a label set is over its labels: content and moments answer
 * the state of work in words of their own, so each half is asked for or against
 * on its own. Both halves have a word called Approved, and the half in front of
 * it is what tells the two apart.
 */
const CONTENT_STATUS_GROUP = { id: 'content_status', label: 'Content status' }
const MOMENT_STATUS_GROUP = { id: 'moment_status', label: 'Moment status' }

/**
 * The id one word of the vocabulary is offered under.
 *
 * The kind is part of the id because both kinds have a word called Approved,
 * and they are two different filters: one is a workflow status on a content
 * item, the other the state of the workflow running over a moment. Without the
 * prefix the address, the tokens and the matcher could not tell them apart.
 */
export const calendarStatusOptionId = (
  kind: CalendarItemKind,
  name: string
): string => `${kind}.${name}`

const contentStatus = (
  status: string,
  label: string
): CalendarFilterOption => ({
  group: CONTENT_STATUS_GROUP,
  id: calendarStatusOptionId('content', status),
  label,
  values: { content: [status] },
})

const momentStatus = (
  name: string,
  label: string,
  value: string
): CalendarFilterOption => ({
  group: MOMENT_STATUS_GROUP,
  id: calendarStatusOptionId('moment', name),
  label,
  values: { moment: [value] },
})

const momentWorkflow = (name: string, summary: WorkflowSummary) =>
  momentStatus(name, summary, workflowValue(summary))

/** The words content carries, in the order the workflow runs through them. */
const CONTENT_STATUS_OPTIONS: readonly CalendarFilterOption[] = [
  contentStatus('approved', 'Approved'),
  contentStatus('for_approval', 'For Approval'),
  contentStatus('needs_revision', 'Needs Revision / Declined'),
  contentStatus('draft', 'Draft'),
]

/**
 * The whole moment vocabulary: the states a workflow can be summarised as, and
 * Idea, which is a moment with no content rather than a state of work.
 */
const MOMENT_STATUS_OPTIONS: readonly CalendarFilterOption[] = [
  momentWorkflow('not_started', WorkflowSummary.NotStarted),
  momentWorkflow('pending_tasks', WorkflowSummary.PendingTasks),
  momentWorkflow('tasks_complete', WorkflowSummary.TasksComplete),
  momentWorkflow('pending_approval', WorkflowSummary.PendingApproval),
  momentWorkflow('approved', WorkflowSummary.Approved),
  momentWorkflow('declined', WorkflowSummary.Declined),
  momentWorkflow('paused', WorkflowSummary.Paused),
  momentStatus('idea', 'Idea', CALENDAR_IDEA_VALUE),
]

/**
 * A half of the vocabulary, as the row that stands for every word under it.
 *
 * It names itself as its own group, the way a channel and a label set do, which
 * is what makes it a row a person can check and open rather than a heading. It
 * spells out the values of the words beneath it for the one kind it speaks
 * about, so choosing the half says exactly what ticking each of those words
 * would — and, like them, says nothing about the other kind.
 */
const statusParent = (
  group: CalendarFilterOptionGroup,
  kind: 'content' | 'moment',
  options: readonly CalendarFilterOption[]
): CalendarFilterOption => ({
  ...group,
  group,
  values: {
    [kind]: options.flatMap((option) => option.values?.[kind] ?? []),
  },
})

/**
 * What a person can filter the state of work by: one category, two halves, and
 * the words of each half under it.
 *
 * Content and moments answer the same question in words of their own, so each
 * word names the values it stands for kind by kind and the matcher never has to
 * know which kind it is looking at. A kind a word says nothing about — Draft on
 * a moment, Paused on a content item — matches nothing, which is also what
 * takes the moment half off the pane while the moment row is off.
 */
export const CALENDAR_STATUS_OPTIONS: readonly CalendarFilterOption[] = [
  statusParent(CONTENT_STATUS_GROUP, 'content', CONTENT_STATUS_OPTIONS),
  ...CONTENT_STATUS_OPTIONS,
  statusParent(MOMENT_STATUS_GROUP, 'moment', MOMENT_STATUS_OPTIONS),
  ...MOMENT_STATUS_OPTIONS,
]

/** Nobody has been asked for the summaries yet, so nothing is known. */
const NO_WORKFLOW_SUMMARIES: CalendarWorkflowSummaries = {
  byKey: new Map(),
  isLoaded: false,
}

/**
 * The state of work an item is in.
 *
 * Content says so itself. A moment is summarised from the workflow running over
 * it, which the calendar does not hold, so until that lookup arrives the
 * category has no opinion about any moment and every one of them passes. Once
 * it has arrived, a moment with no workflow at all has not started.
 */
const statusValues =
  (summaries: CalendarWorkflowSummaries) =>
  (item: CalendarItem): readonly string[] | undefined => {
    if (item.kind !== 'moment') return one(item.statusId)
    if (!summaries.isLoaded) return undefined

    const summary =
      summaries.byKey.get(workflowSummaryKey(item.kind, item.id)) ??
      WorkflowSummary.NotStarted

    return [
      workflowValue(summary),
      ...(item.childCount === 0 ? [CALENDAR_IDEA_VALUE] : []),
    ]
  }

const one = (value?: string) => (value === undefined ? [] : [value])

/** What a category needs from the surface around it to answer for an item. */
export type CalendarCategoryContext = Readonly<{
  workflowSummaries?: CalendarWorkflowSummaries
}>

type StandardCategory = Omit<CalendarFilterCategory, 'options'>

/**
 * The categories in the order the drawer offers them, in the words the week
 * view's filter tray uses: a person who filters both views reads one list.
 */
const standardCategories = ({
  workflowSummaries = NO_WORKFLOW_SUMMARIES,
}: CalendarCategoryContext): readonly StandardCategory[] => [
  // Channels and accounts stay content's alone, even though a moment's payload
  // names the channels under it. A moment is kept by the content that matches,
  // and hides the chips that do not — which is what filtering by channel means
  // to the people who asked for it.
  //
  // Both live in one category because they answer one question. An item carries
  // its channel as well as its account, so choosing a channel means "anything
  // published to it" without the matcher knowing that a channel holds accounts.
  {
    appliesTo: ['content'],
    getValues: (item: CalendarItem) => [
      ...one(item.channelId),
      ...one(item.accountId),
    ],
    icon: 'gem-channel-list',
    id: CalendarFilterCategoryIds.channels,
    label: 'Channels & accounts',
  },
  // Each label set answers for itself. A person filtering by labels is asking
  // for one set and against another in the same breath — the Paid campaigns,
  // but none of the ones for Education — and one mode over the whole category
  // cannot say that. The sets a person matched stack, as they do in the week
  // view and the API.
  {
    appliesTo: ['content', 'moment'],
    getValues: (item: CalendarItem) => item.labelIds ?? [],
    icon: 'gem-label-set',
    id: CalendarFilterCategoryIds.label,
    label: 'Labels',
    matchesEveryParent: true,
    modeScope: 'parent',
  },
  // The two halves answer for themselves, for the same reason the label sets
  // do: a person can want the content that is approved and none of the moments
  // still waiting, and one mode over the whole category cannot say that.
  {
    appliesTo: ['content', 'moment'],
    getValues: statusValues(workflowSummaries),
    icon: 'gem-single-choice',
    id: CalendarFilterCategoryIds.status,
    label: 'Approval status',
    modeScope: 'parent',
  },
  {
    appliesTo: ['content', 'moment', 'story'],
    // A story stands for itself; everything else points at the story it belongs to.
    getValues: (item: CalendarItem) =>
      item.kind === 'story' ? [item.id] : one(item.storyId),
    icon: 'gem-stories',
    id: CalendarFilterCategoryIds.story,
    label: 'Stories',
  },
  {
    appliesTo: ['moment'],
    getValues: (item: CalendarItem) => item.boardIds ?? [],
    icon: 'gem-grid',
    id: CalendarFilterCategoryIds.board,
    label: 'Boards',
  },
  {
    appliesTo: ['content', 'moment', 'story'],
    getValues: (item: CalendarItem) => [
      item.isPrivate === true ? 'private' : 'public',
    ],
    icon: 'gem-locked',
    id: CalendarFilterCategoryIds.privacy,
    label: 'Privacy',
    shouldOfferSelectAll: false,
  },
  // Two dates rather than a list of values: matching reads the item's own
  // window through `matchesLiveRange`, so this category answers for nothing
  // here.
  {
    appliesTo: ['content', 'moment'],
    getValues: () => [],
    icon: 'gem-calendar',
    id: CalendarFilterCategoryIds.live,
    label: 'Live date range',
    picker: 'dateRange',
  },
]

const builtInOptions: Partial<
  Record<CalendarFilterCategoryId, readonly CalendarFilterOption[]>
> = {
  privacy: CALENDAR_PRIVACY_OPTIONS,
  status: CALENDAR_STATUS_OPTIONS,
}

/**
 * Every standard category, carrying only the options that live in code.
 *
 * This is the list to filter items with. Matching reads an option only to learn
 * what an item of a given kind would carry for it, while
 * `buildCalendarFilterCategories` drops a category whose options have not
 * arrived — right for a drawer, which would otherwise offer an empty leaf, and
 * wrong for the matcher, where a filter restored from the address would then
 * quietly hide nothing until its option list loaded.
 */
export const calendarMatchingCategories = (
  context: CalendarCategoryContext = {}
): CalendarFilterCategory[] =>
  standardCategories(context).map((category) => ({
    ...category,
    options: builtInOptions[category.id as CalendarFilterCategoryId] ?? [],
  }))

export type CalendarFilterCategoryOptions = Partial<
  Record<CalendarFilterCategoryId, readonly CalendarFilterOption[]>
>

/**
 * Builds the standard categories with the caller's option data. A category with
 * no options is left out: an empty leaf tells a person nothing, and a workspace
 * with no boards should not offer a board filter. A category a person picks
 * dates in has no options to wait for, so it always stands.
 */
export const buildCalendarFilterCategories = (
  options: CalendarFilterCategoryOptions,
  context: CalendarCategoryContext = {}
): CalendarFilterCategory[] =>
  standardCategories(context)
    .map((category) => ({
      ...category,
      options:
        options[category.id as CalendarFilterCategoryId] ??
        builtInOptions[category.id as CalendarFilterCategoryId] ??
        [],
    }))
    .filter(
      (category) => category.options.length > 0 || category.picker !== undefined
    )

/**
 * Whether an option can speak for any of the kinds on screen.
 *
 * An option that names its values kind by kind says nothing about a kind it
 * does not name, so the moment half of the status list says nothing while the
 * moment row is off. An option that stands for itself speaks for everything.
 */
const speaksForKinds = (
  { values }: CalendarFilterOption,
  shownKinds: readonly CalendarItemKind[]
): boolean =>
  values === undefined ||
  shownKinds.some((kind) => (values[kind] ?? []).length > 0)

/**
 * The categories worth showing for the kinds of item on screen, and the options
 * of each that those kinds can carry. Filtering by channel makes no sense on a
 * calendar of moments alone, and neither does filtering by a moment status on a
 * calendar with the moment row turned off.
 */
export const visibleCalendarFilterCategories = (
  categories: readonly CalendarFilterCategory[],
  shownKinds: readonly CalendarItemKind[]
): CalendarFilterCategory[] =>
  categories
    .filter((category) =>
      category.appliesTo.some((kind) => shownKinds.includes(kind))
    )
    .map((category) => {
      const options = category.options.filter((option) =>
        speaksForKinds(option, shownKinds)
      )

      // The category itself when it offers everything it always did, so the
      // drawer's memos are not rebuilt on every render for nothing.
      return options.length === category.options.length
        ? category
        : { ...category, options }
    })

/**
 * The filters with the values nothing on screen could carry left out.
 *
 * A moment status chosen while the moment row is off stays where it was
 * written — turning the row back on has to bring the filter back with it — but
 * nothing is matched on it. Matching on it would empty the calendar behind a
 * filter the drawer no longer shows.
 *
 * Pass the categories with every option they offer: a value this cannot find is
 * kept, because an option list that has not arrived is not an answer.
 */
export const calendarFiltersForKinds = (
  state: CalendarFilterState,
  categories: readonly CalendarFilterCategory[],
  shownKinds: readonly CalendarItemKind[]
): CalendarFilterState => {
  const byId = new Map(categories.map((category) => [category.id, category]))

  const isSpokenFor = (categoryId: string) => (valueId: string) => {
    const option = byId
      .get(categoryId)
      ?.options.find((each) => each.id === valueId)

    return option === undefined || speaksForKinds(option, shownKinds)
  }

  return {
    ...state,
    cats: Object.fromEntries(
      Object.entries(state.cats).map(([id, category]) => [
        id,
        {
          ...category,
          exclude: category.exclude.filter(isSpokenFor(id)),
          include: category.include.filter(isSpokenFor(id)),
        },
      ])
    ),
  }
}
